import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { FilterMatchMode } from 'primereact/api';
import './MetricsDataTable.scss';
import { useGetAllParentClientsQuery, useGetMetricsQuery } from '../metricsAdminSlice';

interface Metric {
    metricId: number;
    metricName: string;
    label: string;
    parentClient: string;
    metricCategory: string;
    metricDirection: string;
    dataType: string;
    longDescription: string;
    status: string;
}

interface MetricsDataTableProps {
    onCreateMetric?: () => void;
    onEditMetric?: (metricId: number) => void;
}

export const MetricsDataTable: React.FC<MetricsDataTableProps> = ({
     onCreateMetric,
     onEditMetric, 
  }) => {
    const [selectedMetrics, setSelectedMetrics] = useState<Metric[]>([]);
    const [globalMetric, setGlobalMetric] = useState<boolean>(false);
    const [selectedParentClient, setSelectedParentClient] = useState<string>('0');
    const [metricsData, setMetricsData] = useState<any>([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [parentClientsData, setParentClientsData] = useState<any>([]);
    const [filters, setFilters] = useState({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        });
    const { data: parentClients } = useGetAllParentClientsQuery();
    const { data: metrics } = useGetMetricsQuery({
        parentClientId: selectedParentClient || 0,
        globalMetrics: globalMetric,
    });
    useEffect(() => {
        if (parentClients?.data?.length > 0) {
            const allOption = {
            'parent_client_name': 'All',
            'parent_client_id': '0'
        };
            setParentClientsData([allOption, ...parentClients.data]);
        } else {
            setParentClientsData([]);
        }
    }, [parentClients]);
    useEffect(() => {
        if (metrics?.data?.length > 0) {
            setMetricsData(metrics?.data);
        } else {
            setMetricsData([]);
        }
    }, [metrics]);
    
    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        const afilters = { ...filters };
        afilters.global.value = value;
        setFilters(afilters);
        setGlobalFilterValue(value);
    };
    // const actionBodyTemplate = () => {
    //     return (
    //         <Button
    //             type="button"
    //             icon="pi pi-pen-to-square"
    //             className="p-button-text p-button-rounded"
    //         />
    //     );
    // };

        const actionBodyTemplate = (rowData: any) => {  
        return (
            <Button
                type="button"
                icon="pi pi-pen-to-square"
                className="p-button-text p-button-rounded"
                onClick={() => onEditMetric && onEditMetric(rowData.metric_id)}
            />
        );
    };
    const clearFilters = () => {
        setGlobalFilterValue('');
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        });
    }
    const onGlobalMetricChange = (e: any) => {
        clearFilters();
        setMetricsData([]);
        setGlobalMetric(e.checked || false);
        setSelectedParentClient('0');
    };

    const statusBodyTemplate = (rowData: Metric) => {
        return (
            <span className={`status-badge status-${rowData.status.toLowerCase()}`}>
                {rowData.status}
            </span>
        );
    };

    const header = (
        <div className="metrics-table-header">
            <div className="row align-items-center">
                {/* Parent Client Dropdown */}
                <div className="col-md-3">
                    <div className="filter-group">
                        <label htmlFor="parentClient" className="filter-label">
                            Parent Client
                        </label>
                        <Dropdown
                            id="parentClient"
                            value={selectedParentClient}
                            options={parentClientsData || []}
                            onChange={(e) => {setSelectedParentClient(e.value); setGlobalFilterValue('');}}
                            placeholder="Select Parent Client"
                            className="w-100 curvedBorder"
                            disabled={globalMetric}
                            optionLabel='parent_client_name'
                            optionValue='parent_client_id'
                        />
                    </div>
                </div>

                <div className="col-md-3 pt-20">
                    <div className="filter-group d-flex align-items-center">
                        <Checkbox
                            inputId="globalMetric"
                            checked={globalMetric}
                            onChange={onGlobalMetricChange}
                        />
                        <label htmlFor="globalMetric" className="ms-2 mb-0">
                            Global Metric
                        </label>
                    </div>
                </div>

                {/* Search Input */}
                <div className="col-md-4 pt-4">
                    <div className="filter-group">
                        <span className="p-input-icon-left w-100">
                            <i className="pi pi-search" />
                            <InputText
                                placeholder="Keyword Search"
                                className="w-100 curvedBorder"
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                            />
                        </span>
                    </div>
                </div>

                {/* Create New Metric Button */}
                <div className="col-md-2 text-end pt-4">
                    <Button
                        label="Create New Metric"
                        icon="pi pi-plus"
                        className="create-metric-btn curvedBorder"
                        onClick={onCreateMetric}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="metrics-datatable-wrapper">            
            <DataTable
                value={metricsData}
                selection={selectedMetrics}
                onSelectionChange={(e) => setSelectedMetrics(e.value as Metric[])}
                dataKey="metricId"
                paginator={metricsData?.length > 10}
                rows={10}
                rowsPerPageOptions={[10, 20, 50, 100]}
                header={header}
                className="metrics-datatable"
                emptyMessage="No metrics found"
                filters={filters}
                globalFilterFields={['metric_name', 'label', 'parent_client_name', 'metric_category_name', 'metric_direction_name', 'metric_type_name', 'metric_long_desc', 'status']}
            >
                <Column
                    field="metric_name"
                    header="Metric Name"
                    sortable
                    className="metric-name-column"
                />
                <Column
                    field="label"
                    header="Label"
                    sortable
                />
                <Column
                    field="parent_client_name"
                    header="Parent Client"
                    sortable
                />
                <Column
                    field="metric_category_name"
                    header="Metric Category"
                    sortable
                />
                <Column
                    field="metric_direction_name"
                    header="Metric Direction"
                    sortable
                />
                <Column
                    field="metric_type_name"
                    header="Data Type"
                    sortable
                />
                <Column
                    field="metric_long_desc"
                    header="Long Description"
                    className="description-column"
                    sortable
                />
                <Column
                    field="status"
                    header="Status"
                    body={statusBodyTemplate}
                    sortable
                />
                <Column
                    body={actionBodyTemplate}
                    exportable={false}
                    style={{ width: '4rem' }}
                />
            </DataTable>
        </div>
    );
};
